import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "src/ui/shared/Modal/Modal";
import { makeStyles } from "tss-react/mui";

import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box, DialogProps, Typography } from "@mui/material";
import { useDeleteProductChangeLogMutation } from "@products/api";
import { Product } from "@products/types/index";
import { PermissionService } from "@services/permissionService/permissionService";
import SemverService from "@services/semverService/semverService";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { showSuccessToast } from "@shared/toasts/Toasts";

const useStyles = makeStyles()((theme) => ({
    versionBox: {
        marginBottom: theme.spacing(2),
    },
    changeList: {
        paddingLeft: theme.spacing(4),
        marginBottom: theme.spacing(2),
    },
    changeListWithDelete: {
        paddingLeft: theme.spacing(4),
        marginBottom: theme.spacing(2),
    },
    versionHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    version: {
        fontSize: 16,
        fontWeight: "bold",
        flex: 1,
        minWidth: 0,
    },
    versionText: {
        fontSize: 16,
        fontWeight: "bold",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    deleteButtonWrapper: {
        flexShrink: 0,
        width: 40,
        height: 40,
    },
    hiddenButton: {
        visibility: "hidden",
    },
    noChangesText: {
        fontStyle: "italic",
        color: theme.palette.text.secondary,
    },
    editBtn: {
        alignSelf: "flex-end",
    },
}));

interface ChangeLinesProps {
    change: string;
}
const ChangeLines = (props: ChangeLinesProps) => (
    <Box>
        {props.change.split("\n").map((line, lineIndex) => (
            <Typography key={lineIndex} variant="body1">
                {line}
            </Typography>
        ))}
    </Box>
);

interface VersionChangesProps {
    version: string;
    isEditing: boolean;
    changesList: string[];
    productId?: number;
}
const VersionChanges = (props: VersionChangesProps) => {
    const [deleteProductChangeLog] = useDeleteProductChangeLogMutation();

    const { classes } = useStyles();
    const { t } = useTranslation(["modals"]);
    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");

    const handleDelete = async () => {
        if (props.productId) {
            const result = await deleteProductChangeLog({ productId: props.productId, version: props.version });
            if (!("error" in result)) {
                showSuccessToast(t("modals:changesDialog.toast.deletedChangelogSuccess"));
            }
        }
    };

    const isEditing = props.isEditing;
    const hasChanges = props.changesList.length > 0;
    const isDeleteDisabled = cannotManageProducts || !hasChanges;

    return (
        <Box key={props.version} className={classes.versionBox}>
            <Box className={classes.versionHeader}>
                <Typography className={classes.versionText}>{`Version ${props.version}:`}</Typography>
                <Box className={classes.deleteButtonWrapper}>
                    {isEditing ? (
                        <DangerOutlinedButton
                            onClick={handleDelete}
                            disabled={isDeleteDisabled}
                            tooltipProps={{
                                placement: "top",
                                title: isDeleteDisabled ? t("modals:changesDialog.tooltips.cannotDeleteNoChangelog") : "",
                                hide: !isDeleteDisabled,
                            }}
                        >
                            <i className="fa-solid fa-trash" />
                        </DangerOutlinedButton>
                    ) : null}
                </Box>
            </Box>
            <Box className={isEditing ? classes.changeListWithDelete : classes.changeList}>
                {hasChanges ? (
                    props.changesList.map((change, index) => <ChangeLines key={index} change={change} />)
                ) : (
                    <Typography variant="body1" className={classes.noChangesText}>
                        {t("modals:changesDialog.noChanges")}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

interface ChangesListProps {
    isEditing: boolean;
    sortedChanges: [string, string[]][];
    productId?: number;
}
const ChangesList: React.FC<ChangesListProps> = (props: ChangesListProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals"]);
    return (
        <React.Fragment>
            {props.sortedChanges.length > 0 ? (
                props.sortedChanges.map(([version, changesList]) => (
                    <VersionChanges
                        isEditing={props.isEditing}
                        key={version}
                        version={version}
                        changesList={changesList}
                        productId={props.productId}
                    />
                ))
            ) : (
                <Typography variant="body1" className={classes.noChangesText}>
                    {t("modals:changesDialog.noChanges")}
                </Typography>
            )}
        </React.Fragment>
    );
};

interface ChangesDialogProps extends Omit<DialogProps, "onClose"> {
    onClose: () => void;
    changes: Product["changes"] | null;
    productId?: number;
}
export const ChangesDialog = ({ productId, changes, ...props }: ChangesDialogProps) => {
    const { classes } = useStyles();
    const [isEditing, setIsEditing] = useState(false);

    const sortedChanges = changes ? Object.entries(changes).sort(([a], [b]) => SemverService.compare(b, a)) : [];
    const { t } = useTranslation(["modals"]);

    const handleEdit = () => {
        setIsEditing((prev) => !prev);
    };
    const handleOnClose = () => {
        props.onClose();
        setIsEditing(false);
    };
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === "Escape") {
            e.preventDefault();
            handleOnClose();
        }
    };

    return (
        <Modal
            {...props}
            onKeyDown={(e) => onKeyDown(e)}
            onClose={handleOnClose}
            startButton={
                productId &&
                sortedChanges.length > 0 && (
                    <SecondaryButton
                        onClick={handleEdit}
                        className={classes.editBtn}
                        tooltipProps={{ title: t("modals:changesDialog.manageVersionChangelogs") }}
                    >
                        <i className="fa-solid fa-gears" />
                    </SecondaryButton>
                )
            }
        >
            <ChangesList isEditing={isEditing} sortedChanges={sortedChanges} productId={productId} />
        </Modal>
    );
};
